/**
 * Pure helpers for the catalogue-wide model-number scope (REQ-398) of the
 * "Select model number" modal.
 *
 * Everything here is side-effect free and unit-tested directly:
 *  - flattening the nested `GET /model-numbers/` response into rows that carry
 *    their own three-level category path,
 *  - the "has a price for this series" rule,
 *  - relevance ranking + the all-scope sort,
 *  - the category-path presentation split (truncatable ancestors vs. protected leaf).
 *
 * @see types/modelNumbers.type.ts for the response shapes.
 */

import type {
	TCatalogueModelNumber,
	TCategoriesWithModels,
	TCatalogueSuperCategoryNode,
	TCategoryHierarchy,
	TCategoryPath,
	TModelNumber,
	TModelNumberSummary,
	TModelNumbersCatalogue,
} from "types/modelNumbers.type"

/**
 * Characters the user must type before the all-scope search hits the backend.
 * The catalogue holds hundreds of model numbers, so the full list must never be
 * requested or rendered at once (REQ-398 NOTE 2). The design leaves the exact
 * value open between 3 and 4 — change it here, every string and gate reads it.
 */
export const MIN_SEARCH_CHARS = 3

/**
 * Most rows rendered at once, in every scope. The cap is deliberately VISIBLE in
 * the `Results · N` label ("showing first 50 of 128") rather than a silent
 * truncation, so the user knows to refine instead of scrolling for something
 * that was never rendered.
 */
export const MAX_VISIBLE_RESULTS = 50

/** The literal key the backend uses for model numbers that have no category. */
export const WITHOUT_CATEGORY_KEY = "Without category"

/**
 * Turns the `/categories/`-derived `category_hierarchy` (used by the
 * object-category scope) into the same `TCategoryPath` the catalogue scope
 * produces, so both scopes feed one row component.
 */
export const hierarchyToCategoryPath = (hierarchy: TCategoryHierarchy | undefined): TCategoryPath | null => {
	if (!hierarchy) return null
	const topLevel = hierarchy["Top Level Category"]?.name ?? ""
	const superCategory = hierarchy["Sub Category"]?.name ?? ""
	const category = hierarchy["Model Category"]?.name ?? ""
	if (!topLevel && !superCategory && !category) return null
	return { topLevel, superCategory, category }
}

/** Adapts an object-category-scope model (built from `/categories/`) to a catalogue row. */
export const toCatalogueModelNumber = (model: TModelNumber): TCatalogueModelNumber => ({
	id: model.id,
	model_number: model.model_number,
	image_from_stevens_catalogue: model.image_from_stevens_catalogue,
	thumbnails_url: null,
	dimensions: model.dimensions,
	categoryPath: hierarchyToCategoryPath(model.category_hierarchy),
})

/** Narrows a top-level value of the catalogue response to the nested-hierarchy branch. */
const isSuperCategoryNode = (
	value: TCatalogueSuperCategoryNode | TModelNumberSummary[],
): value is TCatalogueSuperCategoryNode => !Array.isArray(value)

/**
 * Flattens the nested `GET /model-numbers/` response into a single row list,
 * resolving each entry's three-level category path from the object keys it was
 * nested under. Entries from the `"Without category"` bucket get
 * `categoryPath: null`.
 *
 * Deduplicated by `id`, first occurrence wins — a categorised entry therefore
 * beats a stray duplicate in the uncategorised bucket.
 */
export const flattenCatalogue = (data: TModelNumbersCatalogue | undefined): TCatalogueModelNumber[] => {
	if (!data) return []
	const seen = new Set<number>()
	const rows: TCatalogueModelNumber[] = []

	const push = (summary: TModelNumberSummary, categoryPath: TCategoryPath | null) => {
		if (!summary || typeof summary.id !== "number" || seen.has(summary.id)) return
		seen.add(summary.id)
		rows.push({ ...summary, categoryPath })
	}

	for (const [topLevelName, topLevelValue] of Object.entries(data)) {
		if (!topLevelValue) continue

		// `"Without category"` maps to a flat array; every other key is the hierarchy.
		if (!isSuperCategoryNode(topLevelValue)) {
			for (const summary of topLevelValue) push(summary, null)
			continue
		}

		for (const [superCategoryName, categories] of Object.entries(topLevelValue)) {
			if (!categories) continue
			for (const [categoryName, summaries] of Object.entries(categories)) {
				if (!Array.isArray(summaries)) continue
				for (const summary of summaries) {
					push(summary, { topLevel: topLevelName, superCategory: superCategoryName, category: categoryName })
				}
			}
		}
	}

	return rows
}

/**
 * Whether `model` has usable pricing for `seriesId` — an entry for that series
 * exists AND carries at least one non-null amount. A `null` `seriesId` means no
 * series is selected, so price availability is undeterminable and we do not
 * flag the row (mirrors the object-category scope's behaviour).
 *
 * Returns `true` when `prices` is absent entirely: that means the caller did not
 * ask for price data, and an unknown price must not render as "No price".
 */
export const hasPriceForSeries = (model: TModelNumberSummary, seriesId: number | null): boolean => {
	if (seriesId === null) return true
	if (!model.prices) return true
	const entry = model.prices.find((price) => price.series?.id === seriesId)
	if (!entry) return false
	return entry.min_price !== null || entry.avg_price !== null || entry.max_price !== null
}

/**
 * How well `modelNumber` answers the typed `term`, lower is better:
 *  0 — exact match, 1 — starts with the term, 2 — contains it, 3 — no match
 *  (in the all-scope: a row the backend matched through its category name only).
 *
 * Relevance leads the all-scope sort: someone who typed `10412` wants `10412`
 * first, with match quality as the tie-breaker rather than the primary key.
 */
export const relevanceRank = (modelNumber: string, term: string): number => {
	const haystack = modelNumber.trim().toLowerCase()
	const needle = term.trim().toLowerCase()
	if (!needle) return 2
	if (haystack === needle) return 0
	if (haystack.startsWith(needle)) return 1
	if (haystack.includes(needle)) return 2
	return 3
}

/**
 * Tag-stack sort weight for the object-category scope, preserving today's
 * match-quality ordering: best match → ML choice → partial → no match.
 */
export const matchQualityRank = (matchTag: "best" | "partial" | "none", isMlChoice: boolean): number => {
	if (matchTag === "best") return 0
	if (isMlChoice) return 1
	if (matchTag === "partial") return 2
	return 3
}

/**
 * Whether selecting `categoryPath` would move the furniture to a different
 * category — the condition for the row's amber dot and the footer's
 * "changes this object's category" notice.
 *
 * Compared on all THREE level names because the payload identifies categories by
 * name only (they are object keys, not ids), and a leaf name alone can repeat
 * across super-categories. An uncategorised model (`null` path) is not a
 * category *change* — it is a rejected save, flagged by its own tag instead.
 */
export const isCategoryChange = (
	categoryPath: TCategoryPath | null,
	current: { topLevel: string | null; superCategory: string | null; category: string | null },
): boolean => {
	if (!categoryPath) return false
	const same = (a: string, b: string | null) => a.trim().toLowerCase() === (b ?? "").trim().toLowerCase()
	return !(
		same(categoryPath.topLevel, current.topLevel) &&
		same(categoryPath.superCategory, current.superCategory) &&
		same(categoryPath.category, current.category)
	)
}

/**
 * Splits a category path for rendering: `ancestors` truncates from the right
 * under pressure while `leaf` is never clipped — the leaf is the one segment
 * that identifies where the model number actually lives, and every result in a
 * search shares long ancestor prefixes.
 */
export const splitCategoryPath = (categoryPath: TCategoryPath): { ancestors: string; leaf: string } => {
	const ancestors = [categoryPath.topLevel, categoryPath.superCategory].filter(Boolean).join(" / ")
	return { ancestors: ancestors ? `${ancestors} / ` : "", leaf: categoryPath.category }
}

/**
 * Where a model number lives in the `/categories/` tree: the node id the create
 * endpoints take as `related_category_id`, plus the two upper level names, which
 * are the tree's own object KEYS — i.e. exactly the values the category selects
 * carry (`useCategorySelection` keys the top two levels by name).
 */
export type TCategoryNodeRef = {
	id: number
	topLevel: string
	superCategory: string
}

/**
 * Finds the category node a model number hangs off, by id.
 *
 * The catalogue-wide scope (REQ-398) returns rows carrying category NAMES rather
 * than ids, so a caller that has to build a `related_category_id` itself — the
 * REQ-293 create panel, which has no furniture to move — cannot use the path on
 * the row. This resolves the id from the taxonomy instead.
 *
 * Returns null for a model number with no category at all (the backend's
 * `"Without category"` bucket, which `/categories/` simply doesn't list).
 *
 * First occurrence wins, matching `flattenCatalogue` and
 * `findModelNumbersForCategory`: a model number shared across peer nodes of one
 * displayed category resolves to whichever peer the payload lists first, and
 * every peer carries the same three names.
 */
export const findCategoryNodeForModelNumber = (
	data: TCategoriesWithModels | undefined,
	modelNumberId: number,
): TCategoryNodeRef | null => {
	if (!data) return null
	for (const [topLevel, superCategories] of Object.entries(data)) {
		for (const [superCategory, nodes] of Object.entries(superCategories)) {
			if (!Array.isArray(nodes)) continue
			for (const node of nodes) {
				const ids = Object.values(node.model_number_to_id ?? {})
				if (ids.includes(modelNumberId)) return { id: node.id, topLevel, superCategory }
			}
		}
	}
	return null
}
